import React, { useEffect, useMemo, useState } from 'react';
import {
  CLIENT_SATISFACTION,
  POC_FOLLOWED,
  SUPERVISED_DISCIPLINES,
  SUPERVISION_COMPETENCIES,
} from '../../../../../dtos/patientmr/charting/supervisory-note.dto';
import type { SupervisoryFormData } from '../../types';
import { codeLabel } from '../../model/entryMeta';
import { formatStaff, StaffMember, useStaffDirectoryContext } from '../../StaffDirectoryContext';
import { CheckboxGroup, FieldsProps, InputField, Segmented, SelectField, TextAreaField } from './fieldParts';

/** RN, LVN (LPN is the older name), HHA or (Phase 20C) a therapy assistant from a provider type; '' otherwise */
export const supervisedDisciplineOf = (credentials: string | null | undefined): string => {
  const c = String(credentials || '').trim().toUpperCase();
  if (c === 'LVN' || c === 'LPN') return 'LVN';
  if (c === 'OTA') return 'COTA';
  if (c === 'CNA' || c === 'AIDE') return 'HHA';
  if (SUPERVISED_DISCIPLINES.includes(c)) return c;
  return '';
};

const isSupervisable = (m: StaffMember) => {
  const d = supervisedDisciplineOf(m.credentials);
  return d !== '' && d !== 'RN';
};

export const POC_LABELS: Record<string, string> = { YES: 'Yes', NO: 'No', PARTIAL: 'Partly' };
export const SATISFACTION_LABELS: Record<string, string> = { SATISFIED: 'Satisfied', CONCERNS: 'Has concerns', NOT_ASSESSED: 'Not assessed' };

/**
 * Phase 19 F21 supervisory note. Staff picker: the patient's LVN / HHA care team members first;
 * when there are none, the tenant's clinical staff.
 */
const SupervisoryNoteFields: React.FC<FieldsProps<SupervisoryFormData> & { currentUserId?: string }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, currentUserId,
}) => {
  const directory = useStaffDirectoryContext();
  const careTeam = useMemo(() => (directory?.careTeam || []).filter((m) => m.userId !== currentUserId), [directory?.careTeam, currentUserId]);
  const teamSupervised = useMemo(() => careTeam.filter(isSupervisable), [careTeam]);
  const [tenantStaff, setTenantStaff] = useState<StaffMember[]>([]);

  useEffect(() => {
    if (teamSupervised.length || !directory) return undefined;
    let cancelled = false;
    directory.loadTenantStaff().then((staff) => {
      if (!cancelled) setTenantStaff(staff.filter((m) => m.userId !== currentUserId));
    });
    return () => {
      cancelled = true;
    };
  }, [teamSupervised.length, directory, currentUserId]);

  const choices = teamSupervised.length
    ? teamSupervised
    : (() => {
        const pool = tenantStaff.length ? tenantStaff : careTeam;
        const supervisable = pool.filter(isSupervisable);
        return supervisable.length ? supervisable : pool;
      })();
  const resolveStaff = (id: string) => choices.find((m) => m.userId === id) || directory?.resolve(id);

  const set = (field: keyof SupervisoryFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-teal-700' };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <SelectField {...common} id="sup-staff" label="Staff supervised" required
          placeholder={choices.length ? 'Select staff member…' : 'No staff found'}
          options={choices.map((m) => ({ value: m.userId, label: formatStaff(m) }))}
          value={value.supervisedUserId}
          onChange={(id) => {
            const picked = resolveStaff(id);
            const discipline = supervisedDisciplineOf(picked?.credentials);
            onChange({ ...value, supervisedUserId: id, supervisedDiscipline: discipline || value.supervisedDiscipline });
          }}
          error={errors.supervisedUserId} />
        <SelectField {...common} id="sup-discipline" label="Discipline" required
          options={SUPERVISED_DISCIPLINES.map((d) => ({ value: d, label: d }))}
          value={value.supervisedDiscipline} onChange={set('supervisedDiscipline')} error={errors.supervisedDiscipline} />
      </div>
      <Segmented name="sup-staff-present" label="Staff present during the visit" required isMobile={isMobile} disabled={disabled}
        options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]}
        value={value.staffPresent} onChange={(v) => onChange({ ...value, staffPresent: v as SupervisoryFormData['staffPresent'] })}
        error={errors.staffPresent} showRequired={showRequired} />
      <Segmented name="sup-poc" label="Plan of care followed" required isMobile={isMobile} disabled={disabled}
        options={POC_FOLLOWED.map((p) => ({ value: p, label: POC_LABELS[p] }))}
        value={value.pocFollowed} onChange={set('pocFollowed')} error={errors.pocFollowed} showRequired={showRequired} />
      <CheckboxGroup name="sup-competencies" label="Competencies observed" disabled={disabled}
        options={SUPERVISION_COMPETENCIES.map((c) => ({ value: c, label: codeLabel(c) }))}
        value={value.competenciesObserved} onChange={(competenciesObserved) => onChange({ ...value, competenciesObserved })} />
      <SelectField {...common} id="sup-satisfaction" label="Patient / family satisfaction"
        options={CLIENT_SATISFACTION.map((c) => ({ value: c, label: SATISFACTION_LABELS[c] }))}
        value={value.clientSatisfaction} onChange={set('clientSatisfaction')} />
      <TextAreaField {...common} id="sup-issues" label="Issues found" rows={2} value={value.issues} onChange={set('issues')} />
      <TextAreaField {...common} id="sup-actions" label="Actions / follow-up" rows={2} value={value.actions} onChange={set('actions')} />
      <InputField {...common} id="sup-next-due" label="Next supervisory visit due" type="date"
        value={value.nextDueDate} onChange={set('nextDueDate')} error={errors.nextDueDate} />
    </div>
  );
};

export default SupervisoryNoteFields;
